import { OperationExecutionMethod } from 'api/types/entity/operation';
import { GridFields } from 'components';
import { DirectoryAutocompleteField } from 'features';
import {
  convertDateByTimezone,
  formatToDateTimeShort,
  formatWithGmtOffset,
  getYesOrNoOptions,
} from 'helpers';
import { useTranslation } from 'libs/i18n';
import { Input } from 'libs/ui';
import {
  Field,
  useFormContext,
} from 'modules/operation/common/forms/operationForm';
import { useOperationFormContentContext } from 'modules/operation/common/providers/OperationFormContentProvider';

import { Section } from '../../Section';

import { TraderField } from './TraderField';

const EXECUTION_PLACE_DIRECTORY_SCHEMA_NAME = 'ExecutionPlace';
const EXECUTION_PLACE_DIRECTORY_FIELD_NAME = 'name';

export const MainInformationSection = () => {
  const { t } = useTranslation();

  const { operationId, brokerTimezoneOffset, createdAt } =
    useOperationFormContentContext();

  const { readOnly, values } = useFormContext();

  return (
    <Section
      datax="operation-form-basic-information-section"
      title={t('operation.sections.mainInformationSection.title')}
    >
      <GridFields>
        {operationId && (
          <Input
            readOnly
            showCopyButton
            disabled={!readOnly}
            label={t(
              'operation.sections.mainInformationSection.fields.operationId.label'
            )}
            value={operationId}
          />
        )}
        {createdAt && (
          <Input
            readOnly
            showCopyButton
            disabled={!readOnly}
            label={t(
              'operation.sections.mainInformationSection.fields.createdAt.label'
            )}
            value={formatWithGmtOffset(
              convertDateByTimezone(createdAt, brokerTimezoneOffset),
              brokerTimezoneOffset,
              formatToDateTimeShort
            )}
          />
        )}
        <Field.Text
          showRequiredMark
          label={t(
            'operation.sections.mainInformationSection.fields.number.label'
          )}
          name="number"
          placeholder={t(
            'operation.sections.mainInformationSection.fields.number.placeholder'
          )}
        />
        {readOnly ? (
          <Input
            readOnly
            showCopyButton
            label={t(
              'operation.sections.mainInformationSection.fields.executedAt.label'
            )}
            value={
              values.executedAt
                ? formatWithGmtOffset(
                    convertDateByTimezone(
                      values.executedAt,
                      brokerTimezoneOffset
                    ),
                    brokerTimezoneOffset,
                    formatToDateTimeShort
                  )
                : ''
            }
          />
        ) : (
          <Field.DatePicker
            showRequiredMark
            label={t(
              'operation.sections.mainInformationSection.fields.executedAt.label'
            )}
            maxDateTime={convertDateByTimezone(
              new Date(),
              brokerTimezoneOffset
            )}
            name="executedAt"
            placeholder={t(
              'operation.sections.mainInformationSection.fields.executedAt.placeholder'
            )}
            variant="date-time"
          />
        )}
        <DirectoryAutocompleteField
          fullWidth
          showRequiredMark
          directorySchemaName={EXECUTION_PLACE_DIRECTORY_SCHEMA_NAME}
          fieldName={EXECUTION_PLACE_DIRECTORY_FIELD_NAME}
          label={t(
            'operation.sections.mainInformationSection.fields.executionPlace.label'
          )}
          name="executionPlace"
          placeholder={t(
            'operation.sections.mainInformationSection.fields.executionPlace.placeholder'
          )}
          readOnly={readOnly}
        />
        <Field.Autocomplete
          fullWidth
          showRequiredMark
          label={t(
            'operation.sections.mainInformationSection.fields.executionMethod.label'
          )}
          name="executionMethod"
          optionLabelKey="label"
          options={Object.values(OperationExecutionMethod).map((v) => ({
            label: t(`operation.operationExecutionMethod.${v}`),
            value: v,
          }))}
          optionValueKey="value"
          placeholder={t(
            'operation.sections.mainInformationSection.fields.executionMethod.placeholder'
          )}
          variant="select"
        />
        <Field.Autocomplete
          fullWidth
          showRequiredMark
          label={t(
            'operation.sections.mainInformationSection.fields.isSpecializedTrade.label'
          )}
          name="isSpecializedTrade"
          optionLabelKey="label"
          options={getYesOrNoOptions(t)}
          optionValueKey="value"
          placeholder={t('common.selectPlaceholder')}
          variant="select"
        />
        <TraderField />
      </GridFields>
    </Section>
  );
};
